import { addMonths, addDays, subDays } from 'date-fns';
import { convertCountryShortcode } from './countries';

export default function anvilIntegration(
  customer,
  facility,
  customerInvoice,
  selectedTitle,
  documentType,
  last4,
  unitContents = null,
  unitRentals = [],
  options = {},
) {
  const dueAmount = customerInvoice
    ? Math.round((customerInvoice.amountToBePaid - customerInvoice.amountPaid) * 100) / 100 : 0;
  const accountBalance = customerInvoice
    ? Math.round((customerInvoice.amount - customerInvoice.amountPaid) * 100) / 100 : 0;

  let monthlyPrice = 0;
  for ( const rental of unitRentals ) {
    monthlyPrice += rental.updatedPrice ? rental.updatedPrice : rental.pricing.price;
  }

  const currentDate = new Date();
  const dueAmountNext = currentDate.getDate() < 20 ? dueAmount + monthlyPrice : accountBalance;

  switch (documentType) {
    case 'PreLien': {
      if (!customerInvoice) {
        throw new Error('Lease not found');
      }
      const preLientDueAmount = customerInvoice.preLienDueAmount;
      return {
        date: new Date(),
        facilityName: facility.account,
        businessName: customer.company,
        unitContents,
        address: () => ({
          city: customer.city,
          state: customer.state,
          country: convertCountryShortcode(customer.country),
          street1: customer.address,
          zip: customer.zip,
        }),
        address2: customer.address2,
        email: customer.email,
        unitNames: customerInvoice.unitsInfo.map(item => item.name).toString().replaceAll(',', ', '),
        facilityAddress3: () => ({
          city: facility.addressInfo.city,
          state: facility.addressInfo.state,
          country: convertCountryShortcode(facility.addressInfo.country),
          street1: facility.addressInfo.addressLine1,
          street2: facility.addressInfo.addressLine2,
          zip: facility.addressInfo.pinCode,
        }),
        dues: Math.round(preLientDueAmount * 100) / 100,
        dueDate: () => {
          const date = new Date();
          const futureDate = addDays(date, 15);
          return new Date(futureDate).toLocaleDateString('en-US');
        },
        dueDate30: () => {
          const date = new Date();
          const futureDate = addDays(date, 30);
          return new Date(futureDate).toLocaleDateString('en-US');
        },
        manager3: facility.manager.name,
        facilityAddress2: () => ({
          city: facility.addressInfo.city,
          state: facility.addressInfo.state,
          country: convertCountryShortcode(facility.addressInfo.country),
          street1: facility.addressInfo.addressLine1,
          street2: facility.addressInfo.addressLine2,
          zip: facility.addressInfo.pinCode,
        }),
        managerPhone: facility.manager.phone,
        facilityEmail: customerInvoice.facility.email,
        manager2: facility.manager.name,
        managerTitle: selectedTitle,
      };
    }
    case 'NonRenewal': {
      return {
        date: new Date(),
        facilityName: facility.account,
        businessName: customer.company,
        address: () => ({
          city: customer.city,
          state: customer.state,
          country: convertCountryShortcode(customer.country),
          street1: customer.address,
          zip: customer.zip,
        }),
        email: customer.email,
        unitNames: customerInvoice.unitsInfo.map(item => item.name).toString().replaceAll(',', ', '),
        facilityAddress3: () => ({
          city: facility.addressInfo.city,
          state: facility.addressInfo.state,
          country: convertCountryShortcode(facility.addressInfo.country),
          street1: facility.addressInfo.addressLine1,
          street2: facility.addressInfo.addressLine2,
          zip: facility.addressInfo.pinCode,
        }),
        facilityEmail: customerInvoice.facility.email,
        managerTitle: selectedTitle,
        selectedEndDate: options.selectedEndDate,
        selectedEndDatePlus1: addDays(options.selectedEndDate, 1),
        depositOnFile: options.depositOnFile,
        newDailyRate: options.newDailyRate,
      };
    }
    case 'Lien': {
      if (!customerInvoice) {
        throw new Error('Lease not found');
      }
      const preLienDocs = customer.docs.filter(item => item.type === 'PRELIMINARY_LIEN_NOTICE' && item.referenceId === customerInvoice._id);
      preLienDocs.sort((item1, item2) => item2.createdAt - item1.createdAt); // sort by latest first
      const [foundDoc] = preLienDocs;
      const date = foundDoc ? new Date(foundDoc.createdAt) : subDays(new Date(), 15);
      const oneMonthAhead = addMonths(new Date(), 1);
      const firstOfNextMonth = new Date(
        oneMonthAhead.getFullYear(),
        oneMonthAhead.getMonth(),
        1,
      ).toLocaleDateString('en-US');

      return {
        // address.city: 'facility.addressInfo.city',
        address: () => ({
          city: customer.city,
          state: customer.state,
          country: convertCountryShortcode(customer.country),
          street1: customer.address,
          zip: customer.zip,
        }),
        address2: customer.address2,
        company: customer.company,
        createdAt: new Date(),
        dueAmount1: Math.round(customerInvoice.preLienDueAmount * 100) / 100,
        dueAmount2: Math.round(customerInvoice.lienDueAmount * 100) / 100,
        dueAmountNext: Math.round(dueAmountNext * 100) / 100,
        email: customer.email,
        facilityAddress1: () => ({
          city: facility.addressInfo.city,
          state: facility.addressInfo.state,
          country: convertCountryShortcode(facility.addressInfo.country),
          street1: facility.addressInfo.addressLine1,
          zip: facility.addressInfo.pinCode,
        }),
        facilityAddress2: () => ({
          city: facility.addressInfo.city,
          state: facility.addressInfo.state,
          country: convertCountryShortcode(facility.addressInfo.country),
          street1: facility.addressInfo.addressLine1,
          street2: facility.addressInfo.addressLine2,
          zip: facility.addressInfo.pinCode,
        }),
        facilityAddress3: () => ({
          city: facility.addressInfo.city,
          state: facility.addressInfo.state,
          country: convertCountryShortcode(facility.addressInfo.country),
          street1: facility.addressInfo.addressLine1,
          street2: facility.addressInfo.addressLine2,
          zip: facility.addressInfo.pinCode,
        }),
        facilityEmail: customerInvoice.facility.email,
        facilityName: facility.account,
        managerName1: facility.manager.name,
        managerName2: facility.manager.name,
        managerPhone: facility.manager.phone,
        managerTitle: selectedTitle,
        next14DaysDate: () => {
          const today = new Date();
          const add14Days = addDays(today, 15);
          return add14Days.toLocaleDateString('en-US');
        },
        next20DaysDate: () => {
          const today = new Date();
          const add20Days = addDays(today, 20);
          return add20Days.toLocaleDateString('en-US');
        },
        next24DaysDate: () => {
          const today = new Date();
          const add24Days = addDays(today, 24);
          return add24Days.toLocaleDateString('en-US');
        },
        next30DaysDate: () => {
          const today = new Date();
          const add30Days = addDays(today, 30);
          return add30Days.toLocaleDateString('en-US');
        },
        next35DaysDate: () => {
          const today = new Date();
          const add35Days = addDays(today, 35);
          return add35Days.toLocaleDateString('en-US');
        },
        nextMonthDay: firstOfNextMonth,
        preLienDate1: () => (date ? date.toLocaleDateString('en-US') : ''),
        preLienDate2: () => addDays(date, 15).toLocaleDateString('en-US'),
        preLienDate3: () => addDays(date, 15).toLocaleDateString('en-US'),
        unitNames: customerInvoice.unitsInfo.map(item => item.name).toString().replaceAll(',', ', '),
        unitContents,
      };
    }
    case 'CreditCard':
      return {
        name: customer.primaryContact.name,
        address: customer.address,
        address2: customer.address2,
        city: customer.city,
        state: customer.state,
        last4,
        email: customer.email,
        phone: customer.phone,
        date: new Date(),
        rentCharges: true,
        fees: true,
        authorize: true,
      };
    default:
      return null;
  }
}



// WEBPACK FOOTER //
// ./src/utils/anvilIntegration.js